import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import ChatContainer from 'modules/Chat/ChatContainer';
import Price from 'components/Price';
import { useTranslation } from 'react-i18next';
import DateFormat from 'components/DateFormat/DateFormat';
import { TOPICS, QUESTIONS_TOPICS, CHAT_ROUTES } from 'utils/constants/chat';
import { useConversation } from 'hooks/useConversation/useConversation';

const ChatNewConversation = ({ className }) => {
  const { t } = useTranslation('Chat');
  const {
    handleGoToRoute,
    handleCreateConversation,
    journeys,
    journeysError,
    loading,
  } = useConversation();
  const [topicSelected, setTopicSelected] = useState(null);

  const renderTopics = () => {
    return Object.keys(QUESTIONS_TOPICS).map((topic) => {
      return (
        <button
          className="chat-new-conversation__button"
          onClick={() =>
            handleCreateConversation(
              QUESTIONS_TOPICS[topic],
              null,
              'Chat new button'
            )
          }
          key={QUESTIONS_TOPICS[topic]}
          data-testid={`chat-new-conversation__topic-${QUESTIONS_TOPICS[topic]}`}
          disabled={loading}
        >
          <span className="chat-new-conversation__button-title">
            {t(QUESTIONS_TOPICS[topic])}
          </span>
        </button>
      );
    });
  };

  const renderJourneys = useCallback(() => {
    if (journeysError) {
      return (
        <div
          className="chat-new-conversation__journey-error"
          data-testid="chat-new-conversation__journey-error"
        >
          {t('journeysError')}
        </div>
      );
    }

    if (!journeys || loading)
      return (
        <>
          {[...Array(7).keys()].map((key) => (
            <button
              className="chat-new-conversation__journey chat-new-conversation__journey--loading"
              key={key}
              data-testid="chat-new-conversation__journey--loading"
            />
          ))}
        </>
      );

    if (journeys.length === 0) {
      return (
        <div
          className="chat-new-conversation__journey-empty"
          data-testid="chat-new-conversation__journey-empty"
        >
          {t('noJourneys')}
        </div>
      );
    }

    return journeys.map((journey) => {
      return (
        <button
          className="chat-new-conversation__journey"
          key={journey.id}
          onClick={() =>
            handleCreateConversation(
              `J${journey.id}`,
              journey.id,
              'Chat journey'
            )
          }
          data-testid="chat-new-conversation__journey"
          disabled={loading}
        >
          <div className="chat-new-conversation__journey-header">
            <DateFormat date={journey.start_time_from} />
          </div>
          <div className="chat-new-conversation__journey-body">{`
          ${journey.country_from || ''} ${journey.label_from} > ${
            journey.country_to || ''
          } ${journey.label_to}`}</div>
          <div className="chat-new-conversation__journey-footer">
            <div className="chat-new-conversation__journey-load-info-container">
              <span className="chat-new-conversation__journey-load-info">{`${journey.max_equivalent_pallets} Eu`}</span>
              <span className="chat-new-conversation__journey-price">
                <Price
                  price={journey.net_full_cost}
                  row
                  currency={journey.currency}
                  small
                  showVat={false}
                />
              </span>
            </div>
            <div className="chat-new-conversation__journey-id">
              {`J${journey.id}`}
            </div>
          </div>
        </button>
      );
    });
  }, [journeys, t, handleCreateConversation, journeysError, loading]);

  return (
    <div className={className} data-testid="chat-new-conversation">
      <div className="chat-new-conversation">
        {!topicSelected && (
          <ChatContainer
            handleBack={() => handleGoToRoute(CHAT_ROUTES.CONVERSATIONS_LIST)}
            title={t('newConversation')}
          >
            <div className="chat-new-conversation__container">
              <button
                data-testid="chat-new-conversation__select-journeys"
                className="chat-new-conversation__button"
                onClick={() => setTopicSelected(TOPICS.JOURNEYS)}
              >
                <span className="chat-new-conversation__button-title">
                  {t('journeyProblems')}
                </span>
                <span className="chat-new-conversation__button-subtitle">
                  {t('journeyProblemsDetail')}
                </span>
              </button>
              <button
                className="chat-new-conversation__button"
                data-testid="chat-new-conversation__select-others"
                onClick={() => setTopicSelected(TOPICS.OTHER_TOPIC)}
              >
                <span className="chat-new-conversation__button-title">
                  {t('otherQuestions')}
                </span>
                <span className="chat-new-conversation__button-subtitle">
                  {t('otherQuestionsDetail')}
                </span>
              </button>
            </div>
          </ChatContainer>
        )}
        {topicSelected === TOPICS.OTHER_TOPIC && (
          <ChatContainer
            handleBack={() => setTopicSelected(null)}
            title={t('newConversation')}
          >
            <div className="chat-new-conversation__container">
              {renderTopics()}
            </div>
          </ChatContainer>
        )}
        {topicSelected === TOPICS.JOURNEYS && (
          <ChatContainer
            handleBack={() => setTopicSelected(null)}
            title={t('journeyProblems')}
            subtitle={t('selectJourney')}
          >
            <div className="chat-new-conversation__container">
              <div className="chat-new-conversation__journey-container">
                {renderJourneys()}
              </div>
            </div>
          </ChatContainer>
        )}
      </div>
    </div>
  );
};
ChatNewConversation.propTypes = {
  className: PropTypes.string,
};
export default ChatNewConversation;
